import { useEffect, useState, useCallback, useRef } from "react";
import { fetchMetaHealth, fetchMetaInsights } from "../services/metaService";
import { metaToWeb, deriveDailyCards } from "../services/metaToWeb";
import {
  CONCEPTS,
  VARIATIONS,
  HOOKS,
  EDGES,
  DAILY_CARDS,
} from "../services/theWebData";
import {
  buildAnalysisIndex,
  buildClusterEdges,
  listWorkspaceAnalyses,
} from "../services/analysisCache";
import {
  TASK_KIND,
  finishTask,
  progressTask,
  startTask,
} from "../stores/statusStore";

/**
 * useMetaWebData
 *
 *   {
 *     status,          "idle" | "loading" | "live" | "fallback" | "error"
 *     source,          "meta" | "seed"
 *     nodes, edges, dailyCards, summary, range,
 *     reload(),        re-pull
 *     setDays(n),      change time range
 *     days,            current days window
 *     errorMessage,    only set when status === "error"
 *   }
 *
 * Strategy:
 *   1. Health check first — cheap, tells us if Meta is configured.
 *   2. If configured, pull insights + adapt → live nodes/edges.
 *   3. On any failure (no token / expired / network) fall back to seed
 *      so the page is always alive.
 */
export function useMetaWebData({ days: initialDays = 30, workspaceId = null } = {}) {
  const [days, setDays] = useState(initialDays);
  const [status, setStatus] = useState("idle");
  const [source, setSource] = useState("seed");
  const [errorMessage, setErrorMessage] = useState("");
  const [graph, setGraph] = useState({
    nodes: [...CONCEPTS, ...VARIATIONS, ...HOOKS],
    edges: EDGES,
    dailyCards: DAILY_CARDS,
    summary: null,
    range: null,
  });

  const seed = useCallback(() => {
    setSource("seed");
    setGraph({
      nodes: [...CONCEPTS, ...VARIATIONS, ...HOOKS],
      edges: EDGES,
      dailyCards: DAILY_CARDS,
      summary: null,
      range: null,
    });
  }, []);

  const runIdRef = useRef(0);

  const load = useCallback(async () => {
    const runId = (runIdRef.current += 1);
    const isStale = () => runId !== runIdRef.current;
    setStatus("loading");
    setErrorMessage("");

    const taskId = `meta-refresh:${days}:${runId}`;
    startTask(taskId, {
      kind: TASK_KIND.META_REFRESH,
      label: `Last ${days} days`,
    });

    let health;
    try {
      health = await fetchMetaHealth();
    } catch (err) {
      if (isStale()) return finishTask(taskId, "done");
      seed();
      setStatus("fallback");
      setErrorMessage(
        `Showing demo data — could not reach Meta: ${String(err?.message || err)}`,
      );
      finishTask(taskId, "done");
      return;
    }

    if (!health?.ok) {
      if (isStale()) return finishTask(taskId, "done");
      seed();
      setStatus("fallback");
      const reason =
        health?.error ||
        (health?.missing?.length
          ? `Missing config: ${health.missing.join(", ")}`
          : "Meta is not connected.");
      setErrorMessage(`Showing demo data — ${reason}`);
      finishTask(taskId, "done");
      return;
    }

    try {
      progressTask(taskId, 0, { label: `Fetching insights · ${days}d` });
      const [payload, analysisRows] = await Promise.all([
        fetchMetaInsights({ days }),
        workspaceId
          ? listWorkspaceAnalyses({ workspaceId }).catch(() => [])
          : Promise.resolve([]),
      ]);
      const analysisIndex = buildAnalysisIndex(analysisRows);
      const built = metaToWeb(payload, { analysisIndex });
      const clusterEdges = buildClusterEdges(analysisRows);
      if (clusterEdges.length) {
        const knownIds = new Set(built.nodes.map((n) => n.id));
        clusterEdges.forEach((edge) => {
          if (knownIds.has(edge.source) && knownIds.has(edge.target)) {
            built.edges.push(edge);
          }
        });
      }
      const cards = deriveDailyCards({ nodes: built.nodes });

      if (isStale()) return finishTask(taskId, "done");

      if (!built.nodes.length) {
        seed();
        setStatus("fallback");
        setErrorMessage("Meta returned no ads for this date range.");
        finishTask(taskId, "done");
        return;
      }

      setSource("meta");
      setGraph({
        nodes: built.nodes,
        edges: built.edges,
        dailyCards: cards.length ? cards : DAILY_CARDS,
        summary: built.summary,
        range: built.range,
      });
      setStatus("live");
      finishTask(taskId, "done");
    } catch (err) {
      if (isStale()) return finishTask(taskId, "done");
      seed();
      setStatus("error");
      setErrorMessage(String(err?.message || err));
      finishTask(taskId, "error", { error: String(err?.message || err) });
    }
  }, [days, seed, workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    status,
    source,
    errorMessage,
    days,
    setDays,
    reload: load,
    ...graph,
  };
}
